import { useEffect, useState } from 'react';
import { useMount } from 'react-use';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@eluve/components';
import { formatSecondsToTime } from '@eluve/date-utils';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';
import { useDialog } from '@eluve/utility-hooks';

import {
  useTranscriptionMachineActor,
  useTranscriptionMachineSelector,
} from '../hooks/useTranscriptionMachine';

const AreYouStillThereDialog = ({
  seconds: initialSeconds,
}: {
  seconds: number;
}) => {
  const { t } = useTranslation();
  const [seconds, setSeconds] = useState<number>(initialSeconds);
  const { isDialogOpen: isOpen, toggleDialog: toggle } = useDialog(true);

  const captureEvent = useCaptureEvent();
  const logger = useNamedLogger('UserPresenceDialog');

  const { send } = useTranscriptionMachineActor();
  const appointmentId = useTranscriptionMachineSelector(
    (snapshot) => snapshot.context.sessionAppointmentId,
  );

  useMount(() => {
    logger.info('UserPresenceDialog shown', { appointmentId });
  });

  useEffect(() => {
    if (seconds === 0) {
      return;
    }

    const intervalId = setInterval(() => {
      setSeconds((previousValue) => previousValue - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [seconds]);

  const onContinue = () => {
    captureEvent('user_presence_session_continued');
    send({ type: 'CONTINUE' });
  };

  const onPause = () => {
    captureEvent('user_presence_session_paused');
    send({ type: 'PAUSE' });
  };

  const handleOpenChange = () => {
    toggle();
    captureEvent('user_presence_dismissed');
    send({ type: 'CONTINUE' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent size="lg" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{t('appointmentPage.presence.title')}</DialogTitle>
          <DialogDescription className="pt-2 pb-1">
            {t('appointmentPage.presence.beforeCountdown')}
            <span className="font-semibold">
              {` ${formatSecondsToTime(seconds, true)} `}
            </span>
            {t('appointmentPage.presence.afterCountdown')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onContinue}>
            {t('appointment.presence.continueRecording')}
          </Button>
          <Button variant="secondary" onClick={onPause}>
            {t('appointment.presence.pauseNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const UserPresenceDialog = () => {
  const shouldCheckUserPresence = useTranscriptionMachineSelector((snapshot) =>
    snapshot.matches({
      InSession: { Recording: { UserPresence: 'Checking' } },
    }),
  );
  const userPresenceResponseWindowMs = useTranscriptionMachineSelector(
    (snapshot) => snapshot.context.userPresenceResponseWindowMs,
  );

  if (!shouldCheckUserPresence) {
    return null;
  }

  return (
    <AreYouStillThereDialog seconds={userPresenceResponseWindowMs / 1000} />
  );
};
